import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, uploadImage } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, Layout } from "lucide-react";

const EMPTY = { about: "", hours: "", services: [], products: [], faqs: [], allow_online_orders: false };

export default function BusinessSite() {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [bizId, setBizId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/businesses/mine").then((r) => {
      if (r.data) { setBizId(r.data.id); if (r.data.site_profile) setForm({ ...EMPTY, ...r.data.site_profile }); }
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setListItem = (list, i, k, v) => setForm((f) => ({ ...f, [list]: f[list].map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = (list, tpl) => setForm((f) => ({ ...f, [list]: [...f[list], tpl] }));
  const removeItem = (list, i) => setForm((f) => ({ ...f, [list]: f[list].filter((_, idx) => idx !== i) }));

  const uploadProductImg = (i) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await uploadImage(file); setListItem("products", i, "image", url); toast.success("Imagen subida"); }
    catch { toast.error("Error al subir"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/businesses/site-profile", {
        about: form.about, hours: form.hours, allow_online_orders: !!form.allow_online_orders,
        services: form.services.map((s) => ({ name: s.name, description: s.description || "", price: s.price === "" || s.price == null ? null : Number(s.price) })),
        products: form.products.map((s) => ({ name: s.name, description: s.description || "", price: s.price === "" || s.price == null ? null : Number(s.price), image: s.image || "" })),
        faqs: form.faqs.map((f) => ({ q: f.q, a: f.a })),
      });
      toast.success("Página web guardada");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const ItemEditor = ({ list, withImage }) => (
    <div className="space-y-3">
      {form[list].map((it, i) => (
        <div key={i} className="rounded-sm border border-slate-200 p-3" data-testid={`${list}-item-${i}`}>
          <div className="flex gap-2">
            <Input placeholder="Nombre" value={it.name} onChange={(e) => setListItem(list, i, "name", e.target.value)} className="flex-1 rounded-sm" data-testid={`${list}-name-${i}`} />
            <Input type="number" placeholder="Precio (opcional)" value={it.price ?? ""} onChange={(e) => setListItem(list, i, "price", e.target.value)} className="w-40 rounded-sm" data-testid={`${list}-price-${i}`} />
            <button type="button" onClick={() => removeItem(list, i)} className="text-slate-300 hover:text-destructive" data-testid={`${list}-del-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <Textarea placeholder="Descripción" value={it.description || ""} onChange={(e) => setListItem(list, i, "description", e.target.value)} className="mt-2 rounded-sm" rows={2} />
          {withImage && (
            <div className="mt-2 flex items-center gap-3">
              {it.image ? <img src={it.image} alt="" className="h-12 w-12 rounded object-cover" /> : null}
              <label className="cursor-pointer text-xs font-semibold text-primary hover:underline">Subir imagen<input type="file" accept="image/*" className="hidden" onChange={uploadProductImg(i)} /></label>
            </div>
          )}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => addItem(list, withImage ? { name: "", description: "", price: null, image: "" } : { name: "", description: "", price: null })} className="rounded-sm" data-testid={`add-${list}`}><Plus className="mr-1 h-3 w-3" />Agregar</Button>
    </div>
  );

  return (
    <div className="max-w-3xl" data-testid="business-site-page">
      <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-secondary"><Layout className="h-6 w-6 text-primary" /> {t("dash_site")}</h1>
      <p className="mt-1 text-sm text-slate-500">Construye tu página empresa. Los clientes te contactan y compran solo por la plataforma, para tu seguridad y la de ellos.</p>

      <div className="mt-6 space-y-6">
        <section className="rounded-sm border border-slate-200 bg-white p-6">
          <h2 className="font-head text-lg font-bold text-secondary">Quiénes somos</h2>
          <Textarea value={form.about} onChange={(e) => set("about", e.target.value)} className="mt-2 rounded-sm" rows={4} placeholder="Describe tu empresa, experiencia, valores…" data-testid="site-about" />
        </section>

        <section className="rounded-sm border border-slate-200 bg-white p-6">
          <h2 className="font-head text-lg font-bold text-secondary">Servicios</h2>
          <div className="mt-3"><ItemEditor list="services" /></div>
        </section>

        <section className="rounded-sm border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-head text-lg font-bold text-secondary">Productos</h2>
            <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={!!form.allow_online_orders} onCheckedChange={(v) => set("allow_online_orders", v)} data-testid="site-online-orders" /> Permitir compra en línea</label>
          </div>
          <div className="mt-3"><ItemEditor list="products" withImage /></div>
        </section>

        <section className="rounded-sm border border-slate-200 bg-white p-6">
          <h2 className="font-head text-lg font-bold text-secondary">Horario de atención</h2>
          <Textarea value={form.hours} onChange={(e) => set("hours", e.target.value)} className="mt-2 rounded-sm" rows={3} placeholder="Lun–Vie 8:00–18:00, Sáb 9:00–13:00…" data-testid="site-hours" />
        </section>

        <section className="rounded-sm border border-slate-200 bg-white p-6">
          <h2 className="font-head text-lg font-bold text-secondary">Preguntas frecuentes</h2>
          <div className="mt-3 space-y-3">
            {form.faqs.map((f, i) => (
              <div key={i} className="rounded-sm border border-slate-200 p-3" data-testid={`faq-item-${i}`}>
                <div className="flex gap-2">
                  <Input placeholder="Pregunta" value={f.q} onChange={(e) => setListItem("faqs", i, "q", e.target.value)} className="flex-1 rounded-sm" />
                  <button type="button" onClick={() => removeItem("faqs", i)} className="text-slate-300 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
                <Textarea placeholder="Respuesta" value={f.a} onChange={(e) => setListItem("faqs", i, "a", e.target.value)} className="mt-2 rounded-sm" rows={2} />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addItem("faqs", { q: "", a: "" })} className="rounded-sm" data-testid="add-faqs"><Plus className="mr-1 h-3 w-3" />Agregar</Button>
          </div>
        </section>

        <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="site-save-btn"><Save className="mr-2 h-4 w-4" />{saving ? "Guardando…" : "Guardar página web"}</Button>
        {bizId && <a href={`/business/${bizId}`} target="_blank" rel="noreferrer" className="ml-3 text-sm font-semibold text-primary hover:underline" data-testid="site-preview-link">Ver mi página pública →</a>}
      </div>
    </div>
  );
}
